import moment from "moment-timezone";
import React, { Fragment } from "react";

import { ACCESS_DENIED_ROLE_MESSAGE } from "@/features/access-control/constants/messages";
import { ACUITY_INDEX } from "@/constants/acuity";
import { Badge2 } from "@/components/UI2/atoms/Badge";
import { Button2 } from "@/ui2/atoms/Button";
import { formatMobileNumber, toTitleCase } from "@/utils/filters";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import Input from "@/components/UI/Input";
import { Tooltip2 } from "@/components/UI2/molecules/Tooltip";
import { useCheckPermission } from "@/features/access-control/hooks/useCheckPermission";

const ActivePatientListItem = (props) => {
  const { badgeVariant, patient, setPatientDetails } = props;

  const canViewPatientChart = useCheckPermission({ permissionKey: "patientDiagnosis" });

  let {
    acuity_score,
    dob,
    first_name,
    initial_video_consult,
    last_name,
    patient_id,
    patient_status,
    preferred_name,
    referred_date,
  } = patient;

  const patientName = first_name + " " + last_name;
  const createdDate = referred_date
    ? moment(parseFloat(referred_date) * 1000).format("MM/DD/YYYY")
    : null;

  if (patient_status === "EXTENDED CARE") {
    patient_status = "Ext. Care";
  }

  const formatDob = (dob) => {
    let [month, day, year] = dob.split("/");

    day = day.replace(/^0+/, "");
    month = month.replace(/^0+/, "");

    return `${month}/${day}/${year}`;
  };

  let desktopListItem = (
    <div className="row no-gutters d-none d-lg-flex fs-14 align-items-center justify-content-between">
      <div className="col-2">
        <span {...getAutomationDataAttr("ActivePatientListItemPatientName")}>
          {`${toTitleCase(patientName)}`}
          {preferred_name ? ` (${toTitleCase(preferred_name)})` : ""},
        </span>
        <span className="DateOfBirth">{formatDob(dob)}</span>
      </div>

      <div className="col-1">
        <span>{acuity_score ?? ""}</span>
        <span className="AcuityDescription">
          {acuity_score ? ` ${ACUITY_INDEX[acuity_score].label}` : null}
        </span>
      </div>

      <div
        className="col-3"
        data-placement="top"
        style={{ overflow: "hidden", textOverflow: "ellipsis" }}
        title={patient.email}
      >
        {patient.email}
      </div>

      <div className="col-1 d-flex tw:justify-center">{formatMobileNumber(patient.mobile)}</div>
      <div className="col-1 d-flex tw:justify-center">{createdDate}</div>

      <div className="col-1 d-flex tw:justify-center">
        {initial_video_consult && (
          <Input
            checked={initial_video_consult ? "checked" : null}
            className="AddPatientInput"
            disabled
            name="initial_video_consult"
            type="checkbox"
          />
        )}
      </div>

      <div className="col-2 d-flex tw:justify-center">
        <Badge2 size="sm" variant={badgeVariant}>
          {patient_status}
        </Badge2>
      </div>

      <div className="col-1 d-flex tw:justify-center">
        {canViewPatientChart ? (
          <Button2
            isDisabled={canViewPatientChart === null}
            onPress={() => setPatientDetails(patient_id, patientName)}
            size="sm"
            variant="secondary"
            {...getAutomationDataAttr("ViewActivePatient")}
          >
            View Chart
          </Button2>
        ) : (
          <Tooltip2 className="tw:max-w-40" placement="top" text={ACCESS_DENIED_ROLE_MESSAGE}>
            <Button2
              isDisabled
              size="sm"
              variant="secondary"
              {...getAutomationDataAttr("ViewActivePatient")}
            >
              View Chart
            </Button2>
          </Tooltip2>
        )}
      </div>
    </div>
  );

  let mobileListItem = (
    <div className="d-flex d-lg-none justify-content-between">
      <div className="d-flex fs-14 flex-column" style={{ width: "65%", wordWrap: "break-word" }}>
        <span className="fs-16 tw:font-bold">{toTitleCase(first_name + " " + last_name)}</span>
        <span>{patient.email}</span>
        <span>{formatMobileNumber(patient.mobile)}</span>
      </div>

      <div className="d-flex fs-14 flex-column">
        <Badge2 size="sm" variant={badgeVariant}>
          {patient_status}
        </Badge2>

        <span>{createdDate}</span>
        <div>{patient.is_spmi ? "SPMI ✓" : null}</div>
        <div>{initial_video_consult ? "First Session ✓" : null}</div>
      </div>
    </div>
  );

  return (
    <Fragment>
      {mobileListItem}
      {desktopListItem}
    </Fragment>
  );
};

export default ActivePatientListItem;
